import { create } from 'zustand'
import type { ProfileWithMembership, AccessPass } from '@/shared/types/app.types'

interface AuthStore {
  userId: string | null
  profile: ProfileWithMembership | null
  isLoading: boolean

  walletBalance: number | null
  activePass: AccessPass | null
  hasAccess: boolean

  // Actions
  setUser: (userId: string | null, profile: ProfileWithMembership | null) => void
  setProfile: (profile: ProfileWithMembership | null) => void
  setWallet: (balance: number | null) => void
  setActivePass: (pass: AccessPass | null) => void
  setLoading: (loading: boolean) => void
  clear: () => void

  // Derived
  isAuthenticated: boolean
  hasActiveMembership: boolean
  isAdmin: boolean
  isStaff: boolean
}

function isPassValid(pass: AccessPass | null): boolean {
  return pass !== null && new Date(pass.expires_at) > new Date()
}

export const useAuthStore = create<AuthStore>()((set) => ({
  userId: null,
  profile: null,
  isLoading: true,
  walletBalance: null,
  activePass: null,
  hasAccess: false,

  setUser: (userId, profile) =>
    set({
      userId,
      profile,
      isLoading: false,
      isAuthenticated: !!userId,
      hasActiveMembership: profile?.membership?.status === 'active',
      hasAccess: (profile?.membership?.status === 'active') || false,
      walletBalance: null,
      activePass: null,
      isAdmin: profile?.role === 'admin',
      isStaff: profile?.role === 'staff' || profile?.role === 'admin',
    }),

  setProfile: (profile) =>
    set((state) => ({
      profile,
      hasActiveMembership: profile?.membership?.status === 'active',
      hasAccess: (profile?.membership?.status === 'active') || isPassValid(state.activePass),
      isAdmin: profile?.role === 'admin',
      isStaff: profile?.role === 'staff' || profile?.role === 'admin',
      isAuthenticated: !!state.userId,
    })),

  setWallet: (balance) =>
    set((state) => ({
      walletBalance: balance,
      hasAccess: state.hasActiveMembership || isPassValid(state.activePass),
    })),

  setActivePass: (pass) =>
    set((state) => ({
      activePass: pass,
      hasAccess: state.hasActiveMembership || isPassValid(pass),
    })),

  setLoading: (loading) => set({ isLoading: loading }),

  clear: () =>
    set({
      userId: null,
      profile: null,
      isLoading: false,
      isAuthenticated: false,
      hasActiveMembership: false,
      hasAccess: false,
      walletBalance: null,
      activePass: null,
      isAdmin: false,
      isStaff: false,
    }),

  // Derived initial values
  isAuthenticated: false,
  hasActiveMembership: false,
  isAdmin: false,
  isStaff: false,
}))
